import { LineItem } from 'types/parts-diagram';
import { MAX_ZOOM } from './constants';

const createCircleElement = (
  cx: number,
  fill: string,
  stroke: string,
  hoverColor: string,
  textElement: SVGTextElement,
  className = '',
  onClick?: () => void,
  onMouseEnter?: (event: MouseEvent) => void,
  onMouseLeave?: (event: MouseEvent) => void
): SVGCircleElement => {
  const circle = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'circle'
  );
  circle.setAttribute('cx', cx.toString());
  circle.setAttribute('cy', '5');
  circle.setAttribute('r', '8');
  circle.setAttribute('fill', fill);
  circle.setAttribute('stroke', stroke);
  circle.setAttribute('transform', textElement.getAttribute('transform') ?? '');
  const originalFill = fill;
  const originalStroke = stroke;
  circle.addEventListener('mouseenter', (event) => {
    onMouseEnter && circle.setAttribute('fill', hoverColor);
    onMouseEnter && circle.setAttribute('stroke', hoverColor);
    if (onMouseEnter) onMouseEnter(event);
  });
  circle.addEventListener('mouseleave', (event) => {
    onMouseLeave && circle.setAttribute('fill', originalFill);
    onMouseLeave && circle.setAttribute('stroke', originalStroke);
    if (onMouseLeave) onMouseLeave(event);
  });

  if (onClick) {
    circle.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
  }
  if (className) {
    circle.classList.add(className);
  }
  return circle;
};

const addCircleToTextElement = (
  textElement: SVGTextElement,
  fill: string,
  stroke: string,
  hoverColor: string,
  className = '',
  onClick?: () => void,
  onMouseEnter?: (event: MouseEvent) => void,
  onMouseLeave?: (event: MouseEvent) => void,
  isSelected?: boolean
) => {
  const textLength = textElement.textContent.length;
  const textContent = textElement.textContent?.trim() || '';

  if (textContent === '-' || textContent === '') {
    return;
  }

  const cx = 3 * textLength;
  const circle = createCircleElement(
    cx,
    fill,
    stroke,
    hoverColor,
    textElement,
    className,
    onClick,
    onMouseEnter,
    onMouseLeave
  );
  textElement.parentNode?.insertBefore(circle, textElement);
};

export const addDefaultCircles = (
  svgDoc: Element,
  referenceNumber: string,
  onClick?: () => void,
  onMouseEnter?: (event: MouseEvent) => void,
  onMouseLeave?: (event: MouseEvent) => void,
  isHover?: boolean,
  isZoomImageEnhancementFlag?: boolean,
  isSelected?: boolean
) => {
  if (referenceNumber === null || referenceNumber === undefined) return;
  const svg = svgDoc.querySelector('svg #CanvasCalloutsGP');
  if (!svg) return;

  const textElements = svg.querySelectorAll(
    `[data-callout="${referenceNumber}"] text, [id^=ID_${referenceNumber}_] > text`
  );
  textElements.forEach((textElement) => {
    textElement.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick && onClick();
    });
    const x = textElement.getAttribute('x');
    const y = textElement.getAttribute('y');
    if (x && y) {
      if (textElement instanceof SVGTextElement) {
        addCircleToTextElement(
          textElement,
          'rgba(255, 255, 255, 0.05)',
          `${isHover && isZoomImageEnhancementFlag && !isSelected ? '#777' : '#000'}`,
          '#777',
          'default-circle',
          onClick,
          onMouseEnter,
          onMouseLeave,
          isSelected
        );
      }
    }
  });
};

export const highlightDiagram = (
  svgDoc: Element,
  hoveredItem: string,
  isZoomImageEnhancementFlag: boolean,
  isSelected = false
) => {
  if (hoveredItem === null || hoveredItem === undefined) return;
  const svg = svgDoc.querySelector('svg #CanvasCalloutsGP');
  if (!svg) return;

  const textElements = svg.querySelectorAll(
    `[data-callout="${hoveredItem}"] text, [id^=ID_${hoveredItem}_] > text`
  );

  textElements.forEach((textElement) => {
    const x = textElement.getAttribute('x');
    const y = textElement.getAttribute('y');
    const fillAndStrokeColor = isZoomImageEnhancementFlag ? '#777' : '#000';
    const circleTextColor = isSelected ? '#000' : fillAndStrokeColor;
    if (x && y) {
      if (textElement instanceof SVGTextElement) {
        textElement.setAttribute('fill', 'white');
        addCircleToTextElement(
          textElement,
          circleTextColor,
          circleTextColor,
          circleTextColor,
          'highlight'
        );
      }
    }
  });
};

export const getDragBounds = (height = 0, width = 0, zoom = 1) => {
  const originalWidth = width / zoom;
  const right = 0.5 * (width - originalWidth);
  const left = -1 * right;

  const originalHeight = height / zoom;
  const bottom = 0.5 * (height - originalHeight);
  const top = -1 * bottom;
  return { bottom, left, right, top };
};

export const diagramZoom = (svgDoc: HTMLElement, zoom: number) => {
  const transform = `scale3d(${zoom}, ${zoom}, ${zoom})`;
  svgDoc.style.transform = transform;
};

export const isValidZoom = (newZoom: number) =>
  newZoom >= 1 && newZoom <= MAX_ZOOM;

export const reOrderPartItems = (
  lineItems: LineItem[],
  givenItem: string
): LineItem[] => {
  const removedItems = lineItems.filter(
    (item) => item?.referenceNumber === givenItem
  );
  const remainingItems = lineItems.filter(
    (item) => item?.referenceNumber !== givenItem
  );
  const sortedRemainingItems = [...remainingItems].sort(
    (a: LineItem, b: LineItem) =>
      Number(a.referenceNumber) - Number(b.referenceNumber)
  );

  const reOrderedParts = [...removedItems, ...sortedRemainingItems];
  return reOrderedParts;
};
export const countValidParts = (arr: LineItem[]) => {
  return arr.filter((item) => item.name).length;
};

export const isValidSvgContent = (content: string): boolean => {
  if (!content || typeof content !== 'string') return false;

  const trimmedContent = content.trim();
  const hasSvgElement = /<svg[\s\S]*<\/svg>/i.test(trimmedContent);
  const startsWithValidSvgFormat = /^(<\?xml[^>]*\?>[\s\S]*)?<svg/i.test(
    trimmedContent
  );
  const htmlDocumentTags = /<(html|head|body|script|link|meta|title)\b/i;
  const hasHtmlDocument = htmlDocumentTags.test(trimmedContent);

  return hasSvgElement && startsWithValidSvgFormat && !hasHtmlDocument;
};

export const zoomToPartNumber = (
  svgDoc: Element,
  partNumber: string,
  setZoom: (zoom: number) => void,
  setDragBounds: (
    bounds:
      | { bottom: number; left: number; right: number; top: number }
      | undefined
  ) => void,
  isDiagramTabActive,
  zoomLevel = 2
) => {
  if (!partNumber || !svgDoc) return;

  const svg = svgDoc.querySelector('svg #CanvasCalloutsGP');
  if (!svg) return;

  // Find all text elements with the given part number
  const textElements = svg.querySelectorAll(
    `[data-callout="${partNumber}"] text, [id^=ID_${partNumber}_] > text`
  );

  if (textElements.length === 0) return;

  // Get SVG dimensions
  const svgElement = svgDoc.querySelector('svg');
  if (!svgElement) return;

  let targetElement = textElements[0];

  if (!isDiagramTabActive && textElements.length > 1) {
    let minDistance = Infinity;

    textElements.forEach((element) => {
      // Reset any existing transforms temporarily to get accurate positions
      const currentTransform = (svgDoc as HTMLElement).style.transform;
      (svgDoc as HTMLElement).style.transform = '';

      const rect = element.getBoundingClientRect();
      const svgElementRect = svgElement.getBoundingClientRect();

      // Restore the transform
      (svgDoc as HTMLElement).style.transform = currentTransform;

      const elementCenterX = rect.left + rect.width / 2 - svgElementRect.left;
      const elementCenterY = rect.top + rect.height / 2 - svgElementRect.top;
      const svgCenterX = svgElementRect.width / 2;
      const svgCenterY = svgElementRect.height / 2;

      const distance = Math.sqrt(
        Math.pow(elementCenterX - svgCenterX, 2) +
          Math.pow(elementCenterY - svgCenterY, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        targetElement = element;
      }
    });
  }

  // Reset transform before calculating new position
  (svgDoc as HTMLElement).style.transform = '';

  // Get the position of the target element
  const elementRect = targetElement.getBoundingClientRect();
  const svgElementRect = svgElement.getBoundingClientRect();
  const elementCenterX =
    elementRect.left + elementRect.width / 2 - svgElementRect.left;
  const elementCenterY =
    elementRect.top + elementRect.height / 2 - svgElementRect.top;
  const svgCenterX = svgElementRect.width / 2;
  const svgCenterY = svgElementRect.height / 2;

  const targetZoom = zoomLevel;

  // Calculate the translation needed to center the element
  const translateX = (svgCenterX - elementCenterX) / targetZoom;
  const translateY = (svgCenterY - elementCenterY) / targetZoom;

  const transform = `scale3d(${targetZoom}, ${targetZoom}, ${targetZoom}) translate3d(${translateX}px, ${translateY}px, 0)`;
  (svgDoc as HTMLElement).style.transform = transform;

  setZoom(targetZoom);

  const height = (svgDoc as HTMLElement).clientHeight;
  const width = (svgDoc as HTMLElement).clientWidth;
  const bounds = getDragBounds(height, width, targetZoom);
  setDragBounds(bounds);
};
